import { useState, useEffect } from 'react';

interface UserDisplayInfo {
  userId: string;
  fullName: string;
  picturePath: string;
  initial: string;
  companyName?: string | null;
}

interface UseUserProfileOptions {
  apiUrl?: string;
  token?: string;
  onError?: (error: Error) => void;
}

export const useUserProfile = (options: UseUserProfileOptions = {}) => {
  const { apiUrl = '/api', token, onError } = options;
  const [userInfo, setUserInfo] = useState<UserDisplayInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchUserProfile = async () => {
      try {
        const response = await fetch(`${apiUrl}/chat/user/display?token=${token}`, {
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user profile (status ${response.status})`);
        }

        const data = await response.json();
        if (controller.signal.aborted) return;
        setUserInfo(data.data);
        setError(null);
      } catch (err) {
        // Ignore aborts (component unmounted or deps changed)
        if (controller.signal.aborted) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        onError?.(error);
        console.error('Error fetching user profile:', error);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchUserProfile();

    return () => controller.abort();
  }, [apiUrl, token, onError]);

  return {
    userInfo,
    isLoading,
    error,
  };
};
